
import React, { useMemo } from 'react';
import { Bot, User, Database, ArrowUpRight } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ResponseTypeIcon } from './ResponseTypeIcon';
import { MessageItemProps } from './types';
import ReactMarkdown from 'react-markdown';
import ChatTable from './ChatTable';
import HybridRenderer from './HybridRenderer';
import ChatExportStatus from './ChatExportStatus';
import { parseMarkdownTable } from './utils/tableParser';
import TokenUsageDisplay from './TokenUsageDisplay';

const MessageItem: React.FC<MessageItemProps> = ({ message, onViewInDataTable }) => {
  const isBot = message.sender === 'bot';
  const isStreaming = message.metadata?.isStreaming;

  const { metadata } = message;

  const formattedTimestamp = format(new Date(message.timestamp), 'h:mm a');

  const sqlQuery = (metadata as any)?.query || '';
  const hasJoins = sqlQuery.toLowerCase().includes(' join ');

  // Memoize table parsing to ensure proper re-renders during streaming
  const tableData = useMemo(() => parseMarkdownTable(message.text), [message.text]);
  const hasTable = tableData && tableData.isTable;

  // Parse total available rows from message text (e.g., "Found 6547 SKUs", "Showing X of Y")
  const totalAvailable = useMemo(() => {
    const match = message.text.match(/(?:Found|Total:|Showing \d+ of)\s+(\d+)\s+(?:SKUs|rows)/i);
    return match ? parseInt(match[1], 10) : null;
  }, [message.text]);

  // Improved error detection - look for actual error patterns, not just the word "error"
  const hasSystemError = () => {
    const text = message.text.toLowerCase();
    const hasResults = (metadata as any)?.results &&
      Array.isArray((metadata as any).results) &&
      (metadata as any).results.length > 0;

    // Check for actual error patterns
    const errorPatterns = [
      'error:', 'failed:', 'exception:', 'unable to', 'could not',
      'permission denied', 'access denied', 'connection failed',
      'syntax error', 'database error', 'query failed'
    ];

    const hasErrorPattern = errorPatterns.some(pattern => text.includes(pattern));

    // Check for SQL queries with no results (actual failures)
    const isSqlFailure = (metadata as any)?.type === 'sql' &&
      (!(metadata as any).results || (metadata as any).results.length === 0) &&
      !text.includes('no results found') && !text.includes('no data');

    // Check for success indicators that override error detection
    const successIndicators = [
      'found', 'showing', 'here are', 'analysis shows', 'results:',
      'total', 'displaying', 'identified'
    ];

    const hasSuccessIndicators = successIndicators.some(indicator => text.includes(indicator));

    // Only mark as error if we have actual error patterns AND no success indicators AND no results
    return (hasErrorPattern || isSqlFailure) && !hasSuccessIndicators && !hasResults;
  };

  const hasError = hasSystemError();

  // Only show "View in Data Table" if:
  // 1. It has results
  // 2. It has table info
  // 3. The SQL query doesn't contain JOIN statements
  // 4. No errors were detected in the response
  const hasResults = (metadata as any)?.results &&
    Array.isArray((metadata as any).results) &&
    (metadata as any).results.length > 0 &&
    (metadata as any).tableInfo &&
    !hasJoins &&
    !hasError;

  const renderConnectionInfo = () => {
    if (!(metadata as any)?.connectionType) return null;

    return (
      <div className="text-xs text-gray-500 mt-1">
        Data source: {(metadata as any).connectionType}
      </div>
    );
  };

  // Minimal text cleaning that preserves markdown syntax
  const cleanMessageText = () => {
    let cleaned = message.text.trim();

    // Only fix critical formatting issues while preserving markdown structure
    // Remove excessive whitespace but keep line breaks for lists and paragraphs
    cleaned = cleaned.replace(/[ \t]+$/gm, ''); // Remove trailing whitespace
    cleaned = cleaned.replace(/\n{4,}/g, '\n\n\n'); // Limit consecutive newlines to 3 max

    return cleaned;
  };

  // Check if the message contains a table
  // Already defined above with useMemo

  return (
    <div className={`flex gap-3 mb-4 ${isBot ? '' : 'flex-row-reverse'}`}>
      <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center 
        ${isBot ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
      >
        {isBot ? <Bot size={16} /> : <User size={16} />}
      </div>

      <div className={`flex flex-col ${isBot ? 'items-start' : 'items-end'}`}>
        <div className={`py-2 px-3 rounded-lg max-w-[80%] break-words
          ${isBot
            ? hasError ? 'bg-red-50 text-red-900' : 'bg-blue-50 text-blue-900'
            : 'bg-gray-200 text-gray-800'}`}
        >
          {(metadata as any)?.type && isBot && (
            <div className="flex items-center gap-1 mb-1">
              <ResponseTypeIcon type={(metadata as any).type} />
              <span className="text-xs text-gray-500">
                {(metadata as any).approach || (metadata as any).type.charAt(0).toUpperCase() + (metadata as any).type.slice(1)}
              </span>
            </div>
          )}

          {isBot && isStreaming && !message.text ? (
            <div className="flex items-center space-x-1 py-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          ) : hasTable ? (
            <div className="space-y-2">
              {/* Extract any text before the table starts */}
              {(() => {
                const lines = message.text.split('\n');
                const firstTableLineIndex = lines.findIndex(line => line.trim().includes('|') && line.trim().includes('-'));
                if (firstTableLineIndex > 0) {
                  const beforeTable = lines.slice(0, firstTableLineIndex - 1).join('\n').trim();
                  if (beforeTable) {
                    return (
                      <div className="text-sm mb-2">
                        <ReactMarkdown>{beforeTable}</ReactMarkdown>
                      </div>
                    );
                  }
                }
                return null;
              })()}
              {/* Render the formatted table with total available count */}
              <ChatTable
                key={`${message.id}-${message.text.length}-${isStreaming ? 'streaming' : 'done'}`}
                tableData={tableData}
                totalAvailable={totalAvailable}
                exportId={metadata?.exportInfo ? (Array.isArray(metadata.exportInfo) ? metadata.exportInfo[0]?.export_id : metadata.exportInfo.export_id) : undefined}
                connectionId={metadata?.exportInfo ? (Array.isArray(metadata.exportInfo) ? metadata.exportInfo[0]?.connection_id : metadata.exportInfo.connection_id) : undefined}
                organisationId={(metadata as any)?.organisation_id}
              />
            </div>
          ) : isBot ? (
            <HybridRenderer text={cleanMessageText()} />
          ) : (
            <div className="whitespace-pre-wrap">
              {cleanMessageText()}
            </div>
          )}

          {renderConnectionInfo()}
        </div>

        {/* Platform Price Preview Data Display */}
        {isBot && metadata?.previewData && Array.isArray(metadata.previewData) && metadata.previewData.length > 0 && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-2 mb-2">
              <Database size={16} className="text-blue-600" />
              <span className="text-sm font-medium text-gray-700">
                Preview Data ({metadata.previewData.length} orders)
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    {metadata.previewData[0] && Object.keys(metadata.previewData[0]).map((key) => (
                      <th key={key} className="text-left p-1 font-medium text-gray-600 min-w-[80px]">
                        {key.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {metadata.previewData.slice(0, 5).map((row, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      {Object.values(row).map((value, cellIndex) => (
                        <td key={cellIndex} className="p-1 text-gray-700">
                          {Array.isArray(value) ?
                            value.join(', ') :
                            value?.toString().length > 20 ?
                              value.toString().substring(0, 20) + '...' :
                              value?.toString() || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {metadata.previewData.length > 5 && (
                <div className="text-center text-xs text-gray-500 mt-2">
                  ... and {metadata.previewData.length - 5} more orders
                </div>
              )}
            </div>
          </div>
        )}

        {/* Export Status Display */}
        {isBot && metadata?.exportInfo && (
          <div className="mt-2 space-y-2">
            {Array.isArray(metadata.exportInfo) ? (
              metadata.exportInfo.map((exportInfo, index) => (
                <div key={exportInfo.export_id || index}>
                  {exportInfo.platform && (
                    <div className="text-xs font-medium text-gray-600 mb-1">
                      {exportInfo.platform} Export
                    </div>
                  )}
                  <ChatExportStatus exportInfo={exportInfo} />
                </div>
              ))
            ) : (
              <ChatExportStatus exportInfo={metadata.exportInfo} />
            )}
          </div>
        )}

        <div className="flex flex-col mt-1">
          <div className="flex items-center">
            <span className="text-xs text-gray-500">{formattedTimestamp}</span>

            {isBot && hasResults && onViewInDataTable && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 text-xs px-2 py-0 h-auto flex items-center text-blue-600 hover:text-blue-800"
                onClick={() => onViewInDataTable(message)}
              >
                <Database size={12} className="mr-1" />
                View in Data Table
                <ArrowUpRight size={12} className="ml-1" />
              </Button>
            )}
          </div>

          {/* Token usage display for bot messages */}
          {isBot && !isStreaming && (
            <TokenUsageDisplay
              inputTokens={metadata?.input_tokens}
              outputTokens={metadata?.output_tokens}
              reasoningTokens={metadata?.reasoning_tokens}
              totalCost={metadata?.total_cost}
              model={metadata?.model}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageItem;
